import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import type { FilesDetailsDto } from '@sharedlib/dtos/dto/files-app/FilesDetailsDto';
import { type Subscription } from 'rxjs';
import { type File } from '../types/file';
import { fileVersionError, type FileVersion, FileVersionsClient } from './file-versions.client';

const LOAD_FAILED = 'Version history could not load. Try again.';

/** A team file's saved versions: each one's download, and Restore content for an editor. */
@Component({
  selector: 'app-file-version-history',
  standalone: true,
  imports: [DateFormatPipe, Button2Component, IconComponent],
  // Through this subtree's space-context client, which stamps the file's own workspace (#2074).
  providers: [FileVersionsClient],
  templateUrl: './file-version-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileVersionHistoryComponent {
  public readonly file = input.required<File>();
  public readonly canEdit = input(false);
  /** The form takes the restored file as its current one. */
  public readonly restored = input<(item: FilesDetailsDto) => void>(() => undefined);
  private readonly client = inject(FileVersionsClient);
  private readonly injector = inject(Injector);
  private readonly space = inject(SpaceContextStore);
  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('restoreCancel');
  private readonly focusCancel = effect(() => {
    const button = this.cancelButton();
    if (!button) return;
    afterNextRender(
      () => {
        if (this.cancelButton() === button) button.nativeElement.focus();
      },
      { injector: this.injector },
    );
  });
  protected readonly available = linkedSignal(() => {
    const file = this.file();
    return !!file.id && this.space.insideTeam() && file.accountId === this.space.contextId();
  });
  protected readonly page = linkedSignal(() => {
    this.file();
    this.space.contextId();
    return 0;
  });
  protected readonly rows = signal<readonly FileVersion[]>([]);
  protected readonly current = signal<number | null>(null);
  protected readonly total = signal(0);
  protected readonly loading = signal(false);
  protected readonly failure = signal<string | null>(null);
  protected readonly confirmation = signal<number | null>(null);
  protected readonly busy = signal(false);
  private readonly reread = signal(0);
  private action?: Subscription;
  private generation = 0;

  private readonly identity = effect(() => {
    this.file();
    this.available();
    this.space.contextId();
    untracked(() => {
      this.generation++;
      this.action?.unsubscribe();
      this.busy.set(false);
      this.confirmation.set(null);
    });
  });
  private readonly load = effect((onCleanup) => {
    const file = this.file();
    const available = this.available();
    const offset = this.page() * 25;
    this.reread();
    untracked(() => {
      this.rows.set([]);
      this.total.set(0);
      this.current.set(null);
      this.failure.set(null);
      this.loading.set(available);
    });
    if (!available) return;
    const subscription = this.client.list(file, offset).subscribe({
      next: (answer) => {
        this.rows.set(answer.items);
        this.current.set(answer.current);
        this.total.set(answer.total);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.failure.set(fileVersionError(error, LOAD_FAILED));
        this.loading.set(false);
      },
    });
    onCleanup(() => subscription.unsubscribe());
  });

  public constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.generation++;
      this.action?.unsubscribe();
    });
  }
  protected review(row: FileVersion): string {
    return row.review === 'requested'
      ? 'Review requested'
      : row.review === 'approved'
        ? 'Approved'
        : 'Changes requested';
  }
  /** The browser saves exactly that version's bytes under its own name, as Download does for the current one. */
  protected download(version: number): void {
    if (this.busy() || !this.available()) return;
    const file = this.file();
    const generation = this.generation;
    this.failure.set(null);
    this.client.read(file, version).subscribe({
      next: (saved) => {
        if (this.generation !== generation) return;
        const link = document.createElement('a');
        link.setAttribute('target', '_self');
        link.setAttribute('href', saved.signedUrl);
        link.setAttribute('download', saved.attachmentName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      },
      error: (error: unknown) => {
        if (this.generation === generation) this.failure.set(fileVersionError(error, 'This version could not be downloaded. Try again.'));
      },
    });
  }
  protected askRestore(version: number): void {
    if (!this.canEdit() || this.busy() || version === this.current()) return;
    this.confirmation.set(version);
    this.failure.set(null);
  }
  protected cancel(): void {
    if (this.busy()) return;
    this.confirmation.set(null);
    this.failure.set(null);
  }
  protected confirmRestore(): void {
    const version = this.confirmation();
    if (version === null || version === this.current() || !this.available() || !this.canEdit() || this.busy()) return;
    const file = this.file();
    const generation = this.generation;
    this.busy.set(true);
    this.failure.set(null);
    this.action = this.client.restore(file, version).subscribe({
      next: ({ item }) => {
        if (this.generation !== generation || this.file() !== file) return;
        this.confirmation.set(null);
        this.busy.set(false);
        this.restored()(item);
        this.page.set(0);
        this.reread.update((value) => value + 1);
      },
      error: (error: unknown) => {
        if (this.generation !== generation) return;
        this.failure.set(fileVersionError(error, 'The version could not be restored. Try again.'));
        this.busy.set(false);
      },
    });
  }
  protected retry(): void {
    if (!this.busy()) this.reread.update((value) => value + 1);
  }
  protected previous(): void {
    if (this.busy()) return;
    this.confirmation.set(null);
    this.page.update((page) => Math.max(0, page - 1));
  }
  protected next(): void {
    if (this.busy() || (this.page() + 1) * 25 >= this.total()) return;
    this.confirmation.set(null);
    this.page.update((page) => page + 1);
  }
}
